import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  ComboBox,
  Grid,
  Modal,
  ModalContent,
  SelectInput,
  TextArea,
  TextInput,
  Typography,
  useToaster
} from "@dealer-center/core-components";
import { useQueryClient } from "@tanstack/react-query";
import { SaveAndCancel } from "@dealer-center/core-custom-components";
import {
  NRT_RECORDS_LIST_QUERY_KEY,
  NRT_USER_PERMISSION_GROUPS_QUERY_KEY,
  fetchNrtRulesForAccountAndProvider,
  isNrtRequestError,
  runNrtRecordTestCheck,
  useCreateNrtRecord,
  useUpdateNrtRecord
} from "@dealer-center/ddc-pages-nrt-database-filter-tool-fetch";
import type {
  CreateNrtRecordRequest,
  NrtClassification,
  NrtRecord,
  NrtRecordFormValidationErrors,
  NrtStatus,
  NrtVehicleType,
  UpdateNrtRecordRequest
} from "@dealer-center/ddc-pages-nrt-database-filter-tool-interface";
import {
  buildInitialNrtRecordFormState,
  buildInitialNrtRuleListResult,
  buildInitialNrtTestResult,
  validateNrtRecordForm
} from "@dealer-center/ddc-pages-nrt-database-filter-tool-interface";
import { parseNrtDate, toUnixTimestampString } from "./nrtDate";
import { RuleListPanel } from "./RuleListPanel";
import { SR_ONLY_SX } from "./srOnlySx";
import { STATUS_LABEL } from "./StatusChip";
import { TestResultPanel } from "./TestResultPanel";
import { ViewLockField } from "./ViewLockField";

// Exported so specs assert the real copy instead of a duplicate that drifts.
export const NOT_ALLOWED_MESSAGE =
  "You are not allowed to save this record. Your access may have changed — " +
  "the page has refreshed your permissions.";

// The creator field is read-only, so this surfaces at the form level instead.
export const MISSING_CREATOR_MESSAGE =
  "We could not confirm who you are signed in as, so this record cannot be " +
  "created. Try reloading the page.";

export interface RecordModalProps {
  // "view" is the read-only Look action: Edit's layout, locked, no Test/Save.
  mode: "add" | "edit" | "view";
  // Stamped onto a new record as its creator; null if the session is unresolved.
  currentUserId?: string | null;
  initialValues?: NrtRecord;
  onClose: () => void;
}

const NOTES_ROWS = 4;

const UNSAVED_HINT_SX = {
  color: "text.secondary",
  fontStyle: "italic",
  textAlign: "right"
} as const;

// type="date" only accepts YYYY-MM-DD; stored dates can be epoch-second.
const toDateInputValue = (value: string | null): string => {
  const date = parseNrtDate(value);
  return date ? date.toISOString().slice(0, 10) : (value ?? "");
};

// Record stores classification as one quoted, comma-separated string.
const CLASSIFICATION_SEPARATOR = ",";

const parseClassifications = (value: string | null): string[] =>
  (value ?? "")
    .split(CLASSIFICATION_SEPARATOR)
    .map(token => token.trim().replace(/^["']|["']$/g, ""))
    .filter(Boolean);

const toClassificationOptions = (value: string | null) => {
  const selected = parseClassifications(value);
  return CLASSIFICATION_OPTIONS.filter(option =>
    selected.includes(option.value)
  );
};

const serializeClassifications = (
  options: readonly { value: string }[]
): string | null =>
  options.map(option => `'${option.value}'`).join(CLASSIFICATION_SEPARATOR) ||
  null;

const STATUS_OPTIONS: { label: string; value: NrtStatus }[] = [
  { label: "Active", value: "ACTIVE" },
  { label: "Inactive", value: "INACTIVE" },
  { label: "Testing", value: "TESTING" }
];

const CLASSIFICATION_OPTIONS: {
  label: string;
  value: Exclude<NrtClassification, null>;
}[] = [
  { label: "All", value: "ALL" },
  { label: "Primary", value: "PRIMARY" },
  { label: "Fleet", value: "FLEET" },
  { label: "Wholesale", value: "WHOLESALE" },
  { label: "Exotic", value: "EXOTIC" },
  { label: "Classifieds", value: "CLASSIFIEDS" }
];

const TYPE_OPTIONS: { label: string; value: Exclude<NrtVehicleType, null> }[] =
  [
    { label: "All", value: "ALL" },
    { label: "New", value: "NEW" },
    { label: "Used", value: "USED" }
  ];

export const RecordModal = ({
  mode,
  currentUserId,
  initialValues,
  onClose
}: RecordModalProps) => {
  const isAddMode = mode === "add";
  const isViewMode = mode === "view";
  const [formState, setFormState] = useState(() => {
    const initialState = buildInitialNrtRecordFormState(initialValues);
    return {
      ...initialState,
      // Add mode stamps the signed-in user; edit mode keeps the original creator.
      createdBy: isAddMode ? (currentUserId ?? "") : initialState.createdBy,
      expirationDate: toDateInputValue(initialState.expirationDate)
    };
  });
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
  const [hasBlurredExpirationDate, setHasBlurredExpirationDate] =
    useState(false);
  const [announcement, setAnnouncement] = useState("");
  const [testResult, setTestResult] = useState(buildInitialNrtTestResult);
  const [ruleListResult, setRuleListResult] = useState(
    buildInitialNrtRuleListResult
  );
  const { toastError, toastSuccess } = useToaster();
  const queryClient = useQueryClient();
  const { mutateAsync: createNrtRecord, isPending: isCreating } =
    useCreateNrtRecord();
  const { mutateAsync: updateNrtRecord, isPending: isUpdating } =
    useUpdateNrtRecord();
  const isSaving = isCreating || isUpdating;

  const validationError = validateNrtRecordForm(formState);

  const classificationComboBoxValue = useMemo(
    () => toClassificationOptions(formState.classification),
    [formState.classification]
  );

  // Baseline to diff against for "dirty" — starts as the opened record, and
  // moves forward to the just-saved values on a successful Save, so the
  // Unsaved-changes hint clears instead of staying stuck on afterward. Every
  // field is a flat string, so a per-key `!==` scan is equivalent to (and
  // much cheaper on every keystroke than) comparing two JSON.stringify
  // snapshots.
  const [lastSavedFormState, setLastSavedFormState] = useState(formState);
  const isDirty = useMemo(
    () =>
      (Object.keys(formState) as (keyof typeof formState)[]).some(
        key => formState[key] !== lastSavedFormState[key]
      ),
    [formState, lastSavedFormState]
  );

  // Aborts the in-flight run when the record closes, so a poll loop cannot
  // outlive the screen that started it.
  const testAbortRef = useRef<AbortController | null>(null);
  // Separate from testAbortRef: the rule-list fetch has its own lifecycle.
  const ruleListAbortRef = useRef<AbortController | null>(null);

  useEffect(
    () => () => {
      testAbortRef.current?.abort();
      ruleListAbortRef.current?.abort();
    },
    []
  );

  // Resolves independently of the vehicle test, so neither blocks the other.
  const fetchRuleList = useCallback((accountId: string, providerId: string) => {
    ruleListAbortRef.current?.abort();
    const controller = new AbortController();
    ruleListAbortRef.current = controller;

    setRuleListResult({
      status: "LOADING",
      rules: [],
      counts: null,
      message: null
    });

    fetchNrtRulesForAccountAndProvider(
      accountId,
      providerId,
      controller.signal
    ).then(result => {
      if (controller.signal.aborted) return;
      setRuleListResult(prev => (prev.status === "LOADING" ? result : prev));
    });
  }, []);

  // Loads once on open for Edit/Look; Add has no saved record to look up.
  useEffect(() => {
    if (isAddMode || !initialValues) return;
    fetchRuleList(initialValues.accountId, initialValues.providerId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Locks every field during either action; close stays unlocked (onHide).
  const isProcessing = testResult.status === "RUNNING" || isSaving;

  // Announces every test outcome via the polite region below (toaster has no
  // aria-live), and additionally raises a page-level toast for the two
  // settled outcomes. One effect, so the two surfaces can't drift apart on a
  // future status change.
  useEffect(() => {
    if (testResult.status === "IDLE") return;

    const label = STATUS_LABEL[testResult.status].toLowerCase();
    setAnnouncement(
      testResult.message
        ? `Test ${label}. ${testResult.message}`
        : `Test ${label}.`
    );

    if (testResult.status === "COMPLETE") {
      toastSuccess(
        "Test complete",
        testResult.message ?? "Test completed successfully."
      );
    } else if (testResult.status === "ERROR") {
      toastError(testResult.message ?? "Test failed.");
    }
  }, [testResult]);

  // Errors stay hidden until the first Save attempt.
  const errorProps = useCallback(
    (field: keyof NrtRecordFormValidationErrors) => {
      const shouldShowError =
        field === "expirationDate"
          ? (hasBlurredExpirationDate || hasAttemptedSubmit) && validationError
          : hasAttemptedSubmit && validationError;
      const message = shouldShowError ? validationError?.[field] : undefined;
      return message ? { errorMessage: message, hasError: true } : {};
    },
    [hasAttemptedSubmit, hasBlurredExpirationDate, validationError]
  );

  const updateField = useCallback(
    <TKey extends keyof typeof formState>(
      field: TKey,
      value: (typeof formState)[TKey]
    ) => {
      // Any field change reverts a shown result to not-yet-tested.
      setTestResult(prev =>
        prev.status === "IDLE" ? prev : buildInitialNrtTestResult()
      );
      if (field === "expirationDate") {
        setHasBlurredExpirationDate(false);
      }
      setFormState(prev => ({ ...prev, [field]: value }));
    },
    []
  );

  // Abandons any in-flight run before closing, so its late result is never
  // applied and polling stops.
  const handleClose = useCallback(() => {
    testAbortRef.current?.abort();
    onClose();
  }, [onClose]);

  // `!initialValues` is a type guard, not a real path — Test is only
  // rendered in Edit mode, the only case it's undefined.
  const handleRunTest = useCallback(async () => {
    if (!initialValues) return;

    const controller = new AbortController();
    testAbortRef.current = controller;

    setTestResult({
      status: "RUNNING",
      timestamp: String(Math.floor(Date.now() / 1000)),
      message: "Running this rule against live inventory data.",
      items: []
    });

    // accountId/providerId are both locked in edit mode, so formState here
    // always matches the saved record — nothing to reconcile with initialValues.
    fetchRuleList(formState.accountId, formState.providerId);

    const result = await runNrtRecordTestCheck(
      {
        accountId: formState.accountId,
        providerId: formState.providerId
      },
      { signal: controller.signal }
    );

    if (controller.signal.aborted) return;
    // Don't resurrect a stale result if a field edit reset to IDLE, or if a
    // newer run superseded this one.
    setTestResult(prev => (prev.status === "RUNNING" ? result : prev));
  }, [initialValues, formState.accountId, formState.providerId, fetchRuleList]);

  const handleSave = useCallback(async () => {
    setHasAttemptedSubmit(true);

    // Ahead of field-level errors: the creator field has no inline message.
    if (isAddMode && !formState.createdBy.trim()) {
      toastError(MISSING_CREATOR_MESSAGE);
      setAnnouncement(MISSING_CREATOR_MESSAGE);
      return;
    }

    if (validationError) return;

    // Required fields are non-blank per validationError, but several are
    // typed `string | null` on formState — hence the casts.
    const payload: CreateNrtRecordRequest = {
      accountId: formState.accountId,
      caseNumber: formState.caseNumber,
      classification: formState.classification as string,
      createdBy: formState.createdBy,
      expirationDate: toUnixTimestampString(formState.expirationDate),
      map: formState.map as string,
      notes: formState.notes,
      providerId: formState.providerId,
      select: formState.select as string,
      status: formState.status,
      tags: formState.tags,
      type: formState.type as NrtVehicleType
    };

    try {
      if (isAddMode) {
        await createNrtRecord(payload);
      } else {
        const id = initialValues?.id;
        if (!id) {
          throw new Error("Missing record id — cannot update this record.");
        }
        const { createdBy, ...updateFields } = payload;
        const updatePayload: UpdateNrtRecordRequest = { id, ...updateFields };
        await updateNrtRecord(updatePayload);
      }

      // Create closes the popup — there's nothing more to do with a
      // just-created record here. Update keeps it open (so the just-saved
      // record can be Tested immediately), so it needs the announcement and
      // the re-baselined dirty check instead.
      if (isAddMode) {
        toastSuccess("Success", "Record created successfully!");
        onClose();
      } else {
        const message = "Record updated successfully!";
        toastSuccess("Success", message);
        setAnnouncement(message);
        setLastSavedFormState(formState);
      }
    } catch (error) {
      // A 403 means the role set this form used is stale — re-read it (FR-019).
      if (isNrtRequestError(error) && error.status === 403) {
        toastError(NOT_ALLOWED_MESSAGE);
        setAnnouncement(NOT_ALLOWED_MESSAGE);
        queryClient.invalidateQueries({
          queryKey: NRT_USER_PERMISSION_GROUPS_QUERY_KEY
        });
        queryClient.invalidateQueries({
          queryKey: NRT_RECORDS_LIST_QUERY_KEY
        });
        return;
      }

      // The form stays open either way — only the notification is transient.
      const errorMessage =
        error instanceof Error
          ? error.message
          : "An error occurred. Please try again.";
      toastError(errorMessage);
      setAnnouncement(errorMessage);
    }
  }, [
    validationError,
    isAddMode,
    formState,
    initialValues,
    createNrtRecord,
    updateNrtRecord,
    queryClient,
    toastError,
    toastSuccess,
    onClose
  ]);

  return (
    // Paper's own scroll would drag the title along with it, so pin Paper
    // and let DialogContent scroll instead, keeping the title fixed.
    <Modal
      show
      header={
        isAddMode
          ? "Add New Record"
          : isViewMode
            ? "View Record"
            : "Edit Record"
      }
      size="fullScreen"
      sx={{
        "& .MuiDialog-paper": { overflowY: "hidden" },
        "& .MuiDialogContent-root": { overflowY: "auto" }
      }}
      // Still dismissible mid-test (abandons the run); blocked only during save.
      onHide={isSaving ? undefined : handleClose}
    >
      <Box aria-live="polite" role="status" sx={SR_ONLY_SX}>
        {announcement}
      </Box>
      <ModalContent>
        {/* One lock for all fields, so a newly added field is covered too. */}
        <ViewLockField isLocked={isViewMode}>
          <Grid container spacing={3}>
            <Grid size={{ md: 4, xs: 12 }}>
              <TextInput
                {...errorProps("accountId")}
                required
                // Identity field: set once at creation, not editable afterward.
                disabled={isProcessing || (!isAddMode && !isViewMode)}
                id="nrt-record-account-id"
                label="Account ID"
                name="accountId"
                value={formState.accountId}
                onChange={e => updateField("accountId", e.target.value)}
              />
            </Grid>

            <Grid size={{ md: 4, xs: 12 }}>
              <TextInput
                {...errorProps("caseNumber")}
                required
                disabled={isProcessing}
                id="nrt-record-case-number"
                label="Case Number"
                name="caseNumber"
                value={formState.caseNumber}
                onChange={e => updateField("caseNumber", e.target.value)}
              />
            </Grid>

            <Grid size={{ md: 4, xs: 12 }}>
              <TextInput
                {...errorProps("providerId")}
                required
                // Identity field: set once at creation, not editable afterward.
                disabled={isProcessing || (!isAddMode && !isViewMode)}
                id="nrt-record-provider-id"
                label="Provider ID"
                name="providerId"
                value={formState.providerId}
                onChange={e => updateField("providerId", e.target.value)}
              />
            </Grid>

            <Grid size={{ md: 4, xs: 12 }}>
              <TextInput
                {...errorProps("createdBy")}
                required
                // Stamped from the session at creation, never user-editable —
                // but only hard-disabled outside View, so Look matches every
                // other field's non-grayed look there and relies on
                // ViewLockField's overlay for the lock.
                disabled={isProcessing || !isViewMode}
                id="nrt-record-created-by"
                label="Created By"
                name="createdBy"
                value={formState.createdBy}
              />
            </Grid>

            <Grid size={{ md: 4, xs: 12 }}>
              <SelectInput
                required
                disabled={isProcessing}
                displayDeselectOption={false}
                id="nrt-record-status"
                label="Status"
                name="status"
                options={STATUS_OPTIONS}
                value={formState.status}
                onChange={e =>
                  updateField("status", e.target.value as NrtStatus)
                }
              />
            </Grid>

            <Grid size={{ md: 4, xs: 12 }}>
              <TextInput
                {...errorProps("expirationDate")}
                required
                disabled={isProcessing}
                id="nrt-record-expiration-date"
                label="Expiration Date"
                name="expirationDate"
                type="date"
                value={formState.expirationDate ?? ""}
                onBlur={() => setHasBlurredExpirationDate(true)}
                onChange={e => updateField("expirationDate", e.target.value)}
              />
            </Grid>

            <Grid size={{ md: 4, xs: 12 }}>
              <ComboBox
                {...errorProps("classification")}
                multiple
                required
                disabled={isProcessing}
                id="nrt-record-classification"
                label="Classification"
                name="classification"
                options={CLASSIFICATION_OPTIONS}
                value={classificationComboBoxValue}
                onChange={e =>
                  updateField(
                    "classification",
                    serializeClassifications(
                      e.target.value ?? []
                    ) as NrtClassification
                  )
                }
              />
            </Grid>

            <Grid size={{ md: 4, xs: 12 }}>
              <SelectInput
                required
                disabled={isProcessing}
                displayDeselectOption={false}
                id="nrt-record-type"
                label="Type"
                name="type"
                options={TYPE_OPTIONS}
                value={formState.type ?? undefined}
                onChange={e =>
                  updateField("type", e.target.value as NrtVehicleType)
                }
              />
            </Grid>

            <Grid size={{ md: 4, xs: 12 }}>
              <TextInput
                disabled={isProcessing}
                id="nrt-record-tags"
                label="Tags"
                name="tags"
                value={formState.tags ?? ""}
                onChange={e => updateField("tags", e.target.value)}
              />
            </Grid>

            {/* Rule expressions and notes can share space. */}
            <Grid size={{ md: 6, xs: 12 }}>
              <TextInput
                {...errorProps("select")}
                required
                disabled={isProcessing}
                id="nrt-record-select"
                label="Select"
                name="select"
                placeholder="e.g. misc01 == 'Active'"
                value={formState.select ?? ""}
                onChange={e => updateField("select", e.target.value)}
              />
            </Grid>

            <Grid size={{ md: 6, xs: 12 }}>
              <TextInput
                id="nrt-record-map"
                {...errorProps("map")}
                required
                disabled={isProcessing}
                label="Map"
                name="map"
                placeholder="e.g. wholesalePrice = 799"
                value={formState.map ?? ""}
                onChange={e => updateField("map", e.target.value)}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <TextArea
                disabled={isProcessing}
                id="nrt-record-notes"
                label="Notes"
                name="notes"
                rows={NOTES_ROWS}
                value={formState.notes ?? ""}
                onChange={e => updateField("notes", e.target.value)}
              />
            </Grid>
          </Grid>
        </ViewLockField>
        {isViewMode ? (
          // Look mode has nothing to save, so Close is the only action.
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              marginBottom: "1rem"
            }}
          >
            <Button
              analyticsConfiguration={{
                eventName: "nrt_mapping_tool_look_close"
              }}
              buttonStyle="secondary"
              onClick={handleClose}
            >
              Close
            </Button>
          </Box>
        ) : testResult.status === "RUNNING" ? (
          // The action row is hidden (not just disabled) while a test runs.
          // Save has its own loading state below instead.
          <Typography sx={{ marginBottom: "1rem" }} variant="body-md">
            Processing…
          </Typography>
        ) : (
          <Box
            sx={{
              alignItems: "flex-end",
              display: "flex",
              justifyContent: "space-between",
              marginBottom: "1rem"
            }}
          >
            {/* Left slot stays in the DOM (empty) in Add mode, so the
            right-hand Save/Cancel box keeps its space-between position. */}
            <Box
              sx={{
                alignItems: "flex-start",
                display: "flex",
                flexDirection: "column",
                gap: "0.25rem"
              }}
            >
              {!isAddMode && (
                <Button
                  analyticsConfiguration={{
                    eventName: "nrt_mapping_tool_run_test"
                  }}
                  buttonStyle="secondary"
                  disabled={isSaving}
                  onClick={handleRunTest}
                >
                  Test
                </Button>
              )}
            </Box>
            <Box
              sx={{
                alignItems: "flex-end",
                display: "flex",
                flexDirection: "column",
                gap: "0.25rem"
              }}
            >
              {isDirty && (
                <Typography
                  data-testid="unsaved-changes-hint"
                  sx={UNSAVED_HINT_SX}
                  variant="body-sm"
                >
                  Unsaved changes
                </Typography>
              )}
              <SaveAndCancel
                analyticsEventName={
                  isAddMode ? "NrtAddRecordForm" : "NrtEditRecordForm"
                }
                isDisabled={{
                  save:
                    !isDirty ||
                    (!!validationError && hasAttemptedSubmit) ||
                    isSaving
                }}
                saveButtonText={
                  isAddMode
                    ? isSaving
                      ? "Creating…"
                      : "Create"
                    : isSaving
                      ? "Saving…"
                      : "Save"
                }
                onCancel={handleClose}
                onSave={handleSave}
              />
            </Box>
          </Box>
        )}
        {!isAddMode && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            {!isViewMode && <TestResultPanel result={testResult} />}
            <RuleListPanel result={ruleListResult} />
          </Box>
        )}
      </ModalContent>
    </Modal>
  );
};
